import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { setAccessToken } from '../lib/api';
import type { AuthedUser } from '../lib/auth';

/**
 * Landing route for SSO callbacks.
 *
 * The API redirects here after a successful Google / Microsoft login:
 *   /auth/sso-callback?accessToken=...&user=...
 *
 * We extract the tokens, store the access token in memory, and
 * dispatch a custom event so the AuthProvider picks up the new user.
 */
export function SsoCallbackRoute() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current) return;
    handled.current = true;

    const accessToken = params.get('accessToken');
    const userJson = params.get('user');

    if (!accessToken || !userJson) {
      // Something went wrong — send back to login
      navigate('/login?error=sso_failed', { replace: true });
      return;
    }

    try {
      const user = JSON.parse(decodeURIComponent(userJson)) as AuthedUser;
      setAccessToken(accessToken);

      // Dispatch a custom event that AuthProvider listens to
      window.dispatchEvent(
        new CustomEvent('auth:sso-login', { detail: { accessToken, user } }),
      );

      navigate('/dashboard', { replace: true });
    } catch {
      navigate('/login?error=sso_failed', { replace: true });
    }
  }, [params, navigate]);

  return (
    <div className="grid h-full place-items-center text-sm text-neutral-400">
      Signing you in...
    </div>
  );
}
